import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'wb-theme';

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Resolve the theme to apply on first paint (stored preference → light).
 *
 * Light is the product default and the system preference is deliberately NOT
 * followed: the palette is a warm-paper design tuned for bright station platforms,
 * and an operator who wants dark picks it once on /account and it persists. This
 * also matches the pre-paint script in index.html, which only ever applies dark
 * when it is explicitly stored — consulting the system here made a dark-mode OS
 * flash light, then dark, on every cold load.
 */
export function resolveInitialTheme(): Theme {
  return readStored() ?? 'light';
}

/** Toggle the `.dark` class on <html> to match `theme`. */
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
}

// --- external store so every consumer stays in sync ---
const listeners = new Set<() => void>();
let current: Theme = typeof document !== 'undefined' ? resolveInitialTheme() : 'light';

function emit() {
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): Theme {
  return current;
}

/** Apply the initial theme as early as possible (called from main.tsx). */
export function initTheme(): void {
  current = resolveInitialTheme();
  applyTheme(current);
}

export function setTheme(theme: Theme): void {
  current = theme;
  applyTheme(theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore persistence failures (private mode, etc.) */
  }
  emit();
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void } {
  const theme = useSyncExternalStore(subscribe, getSnapshot, () => 'light' as Theme);
  const set = useCallback((t: Theme) => setTheme(t), []);
  return { theme, setTheme: set };
}
